import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Input } from '../ui/input';
import { ScrollArea } from '../ui/scroll-area';
import { useToast } from '../ui/use-toast';
import { Lock } from 'lucide-react';

type TaskType = 'inference' | 'embedding' | 'web-search' | 'analysis';

const models = ['gpt-4o', 'gpt-4o-mini', 'llama3', 'gemini-1.5-pro', 'deepseek-r1'];

const AIIsolation: React.FC = () => {
  const { toast } = useToast();

  const [taskType, setTaskType] = useState<TaskType>('inference');
  const [taskName, setTaskName] = useState<string>('Isolated Task');
  const [model, setModel] = useState<string>(models[0]);

  const [simulate, setSimulate] = useState<boolean>(true);
  const [writeAccess, setWriteAccess] = useState<boolean>(false);
  const [reasoning, setReasoning] = useState<boolean>(false);

  const startIsolatedTask = () => {
    const detail = {
      name: taskName.trim() || 'Isolated Task',
      type: taskType,
      model,
      restrictions: {
        simulate,
        writeAccess,
        reasoning
      },
      source: 'AIIsolation'
    };

    window.dispatchEvent(new CustomEvent('mapleos-start-isolated-task', { detail }));

    toast({
      title: 'Isolated task started',
      description: 'The AI task is running in a sandbox with your chosen restrictions.'
    });
  };

  return (
    <div className="h-full w-full flex flex-col">
      <Card className="border-0 rounded-none">
        <CardHeader className="py-4">
          <div className="flex items-center gap-2">
            <Lock className="w-5 h-5" />
            <CardTitle className="text-lg">AI Isolation</CardTitle>
            <Badge variant="secondary" className="ml-2">Jail</Badge>
          </div>
          <CardDescription className="mt-1">
            Start AI tasks in a sandbox to prevent OS or file damage. Configure simulation, write access, and reasoning.
          </CardDescription>
        </CardHeader>
      </Card>

      <ScrollArea className="flex-1">
        <div className="p-4 space-y-4 max-w-2xl">
          <Card>
            <CardContent className="p-4 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <div className="text-sm font-medium">Task type</div>
                  <Select value={taskType} onValueChange={(v) => setTaskType(v as TaskType)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select task type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="inference">Inference</SelectItem>
                      <SelectItem value="embedding">Embedding</SelectItem>
                      <SelectItem value="web-search">Web Search</SelectItem>
                      <SelectItem value="analysis">Analysis</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <div className="text-sm font-medium">Model</div>
                  <Select value={model} onValueChange={setModel}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a model" />
                    </SelectTrigger>
                    <SelectContent>
                      {models.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2 sm:col-span-2">
                  <div className="text-sm font-medium">Task name</div>
                  <Input value={taskName} onChange={(e) => setTaskName(e.target.value)} placeholder="e.g., Sandbox: summarize report" />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex items-center justify-between rounded-md border p-3">
                  <div>
                    <div className="text-sm font-medium">Simulate task</div>
                    <div className="text-xs text-muted-foreground">No side effects. Use synthetic data/output.</div>
                  </div>
                  <Switch checked={simulate} onCheckedChange={setSimulate} />
                </div>

                <div className="flex items-center justify-between rounded-md border p-3">
                  <div>
                    <div className="text-sm font-medium">Allow write access</div>
                    <div className="text-xs text-muted-foreground">Permit writes to sandboxed storage only.</div>
                  </div>
                  <Switch checked={writeAccess} onCheckedChange={setWriteAccess} />
                </div>

                <div className="flex items-center justify-between rounded-md border p-3 sm:col-span-2">
                  <div>
                    <div className="text-sm font-medium">Allow reasoning</div>
                    <div className="text-xs text-muted-foreground">Enable chain-of-thought style planning/reasoning.</div>
                  </div>
                  <Switch checked={reasoning} onCheckedChange={setReasoning} />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Button onClick={startIsolatedTask}>Start isolated task</Button>
                <Badge variant="secondary" className="text-xs">Sandboxed</Badge>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 text-sm text-muted-foreground">
              Notes:
              <ul className="list-disc pl-5 mt-2 space-y-1">
                <li>Isolated tasks appear in AI Task Manager with an “Isolated” badge and their restrictions listed.</li>
                <li>Simulation prevents real changes; write access is limited; reasoning toggles high-level planning.</li>
                <li>This jail is per-task and doesn’t change global AI access (use Stop/Resume in AI Center for global control).</li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </ScrollArea>
    </div>
  );
};

export default AIIsolation;